import type { HotspotImageBlock, LocaleCode } from '@fixit/bozp-content';
import { useRef, useState } from 'react';
import { resolveLocalized } from '../locale';

/** Interactive engagement widget — reports the first hotspot tap via onInteract(block.id). */
export function HotspotImageBlockView({
  block,
  locale,
  onInteract,
}: {
  block: HotspotImageBlock;
  locale: LocaleCode;
  onInteract?: (widgetId: string) => void;
}) {
  const fired = useRef(false);
  const [imageFailed, setImageFailed] = useState(false);
  const reportInteraction = () => {
    if (fired.current) return;
    fired.current = true;
    onInteract?.(block.id);
  };

  // Image unavailable (offline, asset not yet uploaded): the absolutely-positioned
  // hotspots would collapse into an unusable strip, AND the chapter gate can require
  // this widget's interaction — degrade to a tappable list so the worker is never stuck.
  if (imageFailed) {
    return (
      <div data-block-type="hotspotImage" data-image-failed="true">
        <ul>
          {block.hotspots.map((hs) => (
            <li key={hs.id}>
              <button
                type="button"
                data-hotspot-id={hs.id}
                onClick={reportInteraction}
                style={{ textAlign: 'left' }}
              >
                {resolveLocalized(hs.label, locale)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div data-block-type="hotspotImage" style={{ position: 'relative', display: 'inline-block' }}>
      <img src={block.src} alt="" onError={() => setImageFailed(true)} />
      {block.hotspots.map((hs) => (
        <button
          key={hs.id}
          type="button"
          data-hotspot-id={hs.id}
          onClick={reportInteraction}
          style={{ position: 'absolute', left: `${hs.x * 100}%`, top: `${hs.y * 100}%` }}
        >
          {resolveLocalized(hs.label, locale)}
        </button>
      ))}
    </div>
  );
}
